/* eslint-disable react/button-has-type */
/* eslint-disable import/extensions */
import React, { useEffect, useState, useRef } from "react";
import logger from "../../utils/logger.js";
import InfoIcon from '@mui/icons-material/Info';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { getSenderColor } from '../../utils/senderColor';
import { IMessage, FileBlock, DocumentCitationBlock } from "../../models/Message";
import { useCanvasPanel } from '../../contexts/CanvasPanelContext';
import { useStreamingMessage } from '../../hooks/useStreamingMessage';
import {
  Avatar,
  Box,
  Stack,
  styled,
  Paper,
  Typography,
  Chip,
  IconButton,
  Fade,
  alpha,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Rating
} from "@mui/material";
import { toast } from 'react-toastify';
import { getFileURL, getRagSourceBlob, getRagSourceURL, toggleReaction } from '../../resources/api.resource.ts';
import ContentBlockRenderer from './ContentBlockRenderer';
import featureFlagService from '../../services/featureFlagService';


interface MessageComponentProps {
  message: IMessage;
  uId: string;
  chatType: string;
  collaborationId: string;
  collaborationIdSummary?: string;
  tenantId: string;
  allUsersMap?: Record<string, { name: string; email: string; userType: 'user' | 'agent' }>;
  /** Shared WebSocket from Workspaces — only provided for streaming messages */
  socket?: WebSocket;
}

// Styled components using Material-UI theme
const ASSISTANT_COLOR = '#7C3AED';

const MessageBubble = styled(Paper)<{ isUser: boolean; isAssistant: boolean; isSystem: boolean; accentColor?: string }>(
  ({ theme, isUser, isAssistant, isSystem, accentColor }) => ({
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    maxWidth: '100%',
    wordWrap: 'break-word',
    ...(isSystem && {
      backgroundColor: alpha(theme.palette.info.main, 0.1),
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    }),
    ...(isUser && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    }),
    ...(isAssistant && {
      backgroundColor: alpha(ASSISTANT_COLOR, 0.07),
    }),
    ...(!isUser && !isAssistant && !isSystem && {
      backgroundColor: theme.palette.background.paper,
      borderLeft: accentColor ? `4px solid ${accentColor}` : undefined,
      paddingLeft: accentColor ? theme.spacing(1.5) : undefined,
    }),
  })
);

const ReactionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  minWidth: 'auto',
  fontSize: '1.2rem',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.2)',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const ReactionsContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(3),
  boxShadow: theme.shadows[2],
  backgroundColor: theme.palette.background.paper,
}));

const MessageComponent: React.FC<MessageComponentProps> = ({ message, uId, chatType, collaborationId, collaborationIdSummary, tenantId, allUsersMap = {}, socket }) => {
  const { contentBlocks: streamingBlocks, isStreaming, error: streamError } = useStreamingMessage({
    message,
    socket: socket ?? null,
    collaborationId,
    enabled: message.status === 'streaming',
  });

  const displayBlocks = message.status === 'streaming' ? streamingBlocks : message.contentBlocks;
  const [align, setAlign] = useState("left");
  const [copied, setCopied] = useState(false);
  const [systemMessage] = useState(message?.sender?.role === "system");
  const [showCheckIcon, setShowCheckIcon] = useState(false);
  const [showReactionsMenu, setShowReactionsMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState<string>("");
  const [feedbackText, setFeedbackText] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const reactionMenuRef = useRef<HTMLDivElement>(null);
  const citationPreviewObjectUrlRef = useRef<string | null>(null);
  const [aiMetadataAnchor, setAiMetadataAnchor] = useState<{ top: number; left: number } | null>(null);
  const [aiMetadataHoverTimeout, setAiMetadataHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isHoveringAiMetadata, setIsHoveringAiMetadata] = useState(false);
  const [loadingFileName, setLoadingFileName] = useState<string | null>(null);
  const canvas = useCanvasPanel();

  useEffect(() => {
    return () => {
      if (citationPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(citationPreviewObjectUrlRef.current);
        citationPreviewObjectUrlRef.current = null;
      }
    };
  }, []);

  const availableReactions = ['👍', '👎', '❤️', '😂', '😮', '😢', '😡'];

  useEffect(() => {
    if(!message.sender.id) return;
    setAlign(message.sender.id.toUpperCase() === uId.toUpperCase() ? "right" : "left");
  }, [message, uId]);


  const handleReaction = async (emoji: string) => {
    // Close menu after reaction click
    setIsHovered(false);
    setShowReactionsMenu(false);

    // For assistant messages with thumbs up/down, show feedback dialog
    if (isAssistantMessage && (emoji === '👍' || emoji === '👎')) {
      setSelectedEmoji(emoji);
      setShowFeedbackDialog(true);
      return;
    }

    // For other reactions, call API immediately
    try {
      await toggleReaction({
        collaboration_id: collaborationId,
        message_id: message.id,
        emoji: emoji,
        action: "toggle",
        feedback: "",
        rating: null
      });

      // Log analytics event
      logger.event("Reaction Added", {
        messageId: message.id,
        emoji,
        userId: uId
      });

      // Note: WebSocket will broadcast message_reaction event to update UI
      // No manual cache update needed - ChatPanel handles message_reaction events
    } catch (err: any) {
      console.error('Failed to toggle reaction:', err);
      toast.error('Failed to update reaction. Please try again.');
    }
  };

  const handleFeedbackSubmit = async () => {
    // Map the 1-5 UI rating to the appropriate scale
    let mappedRating: number | null = null;
    if (rating !== null) {
      if (selectedEmoji === '👍') {
        // Thumbs up: 1→0, 2→0.25, 3→0.5, 4→0.75, 5→1
        mappedRating = (rating - 1) * 0.25;
      } else {
        // Thumbs down: 1→0, 2→-0.25, 3→-0.5, 4→-0.75, 5→-1
        mappedRating = (rating - 1) * -0.25;
      }
    }

    try {
      await toggleReaction({
        collaboration_id: collaborationId,
        message_id: message.id,
        emoji: selectedEmoji,
        action: "toggle",
        feedback: feedbackText,
        rating: mappedRating
      });

      // Log analytics event
      logger.event("Reaction Added", {
        messageId: message.id,
        emoji: selectedEmoji,
        userId: uId,
        feedbackProvided: feedbackText.length > 0,
        rating: mappedRating
      });

      // Close dialog and reset state
      setShowFeedbackDialog(false);
      setSelectedEmoji("");
      setFeedbackText("");
      setRating(null);
    } catch (err: any) {
      console.error('Failed to toggle reaction:', err);
      toast.error('Failed to update reaction. Please try again.');
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackDialog(false);
    setSelectedEmoji("");
    setFeedbackText("");
    setRating(null);
  };

  const handleCopy = async () => {
    try {
      const messageElement = messageRef.current;
      if (!messageElement) return;

      const htmlContent = messageElement.innerHTML;
      const plainText = messageElement.textContent;

      if (window.ClipboardItem && navigator.clipboard.write) {
        const blobHtml = new Blob([htmlContent], { type: "text/html" });
        const blobText = new Blob([plainText], { type: "text/plain" });

        const clipboardItem = new ClipboardItem({
          "text/html": blobHtml,
          "text/plain": blobText,
        });

        await navigator.clipboard.write([clipboardItem]);
      } else {
        // fallback
        await navigator.clipboard.writeText(plainText);
      }

      setCopied(true);
      setShowCheckIcon(true);
      setTimeout(() => {
        setCopied(false);
        setShowCheckIcon(false);
      }, 2000);
      logger.event("Message Copied", { html: htmlContent });
    } catch (err: any) {
      logger.error("Failed to copy formatted text", err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reactionMenuRef.current && !reactionMenuRef.current.contains(event.target as Node)) {
        setShowReactionsMenu(false);
      }
    };

    if (showReactionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showReactionsMenu]);

  const handleAiMetadataMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (aiMetadataHoverTimeout) clearTimeout(aiMetadataHoverTimeout);
    setIsHoveringAiMetadata(true);

    const rect = event.currentTarget.getBoundingClientRect();
    const timeout = setTimeout(() => {
      setAiMetadataAnchor({
        top: rect.top + window.scrollY - 8,
        left: rect.left + window.scrollX,
      });
    }, 300);

    setAiMetadataHoverTimeout(timeout);
  };

  const handleAiMetadataMouseLeave = () => {
    if (aiMetadataHoverTimeout) {
      clearTimeout(aiMetadataHoverTimeout);
      setAiMetadataHoverTimeout(null);
    }
    setIsHoveringAiMetadata(false);
    setTimeout(() => {
      setAiMetadataAnchor(null);
    }, 100);
  };

  const handleAiMetadataClose = () => {
    setAiMetadataAnchor(null);
    setIsHoveringAiMetadata(false);
  };

  const API_CODE = process.env.REACT_APP_API_CODE;

  const getFileObj = (fileBlock: FileBlock) => {
    const parts = fileBlock.url?.split("/") ?? [];
    const collaborationsIdx = parts.indexOf("collaborations");
    const url_collaboration_id =
      collaborationsIdx >= 0 && parts.length > collaborationsIdx + 1
        ? parts[collaborationsIdx + 1]
        : undefined;
    const instancesIdx = parts.indexOf("instances");
    const url_instance_id =
      instancesIdx >= 0 && parts.length > instancesIdx + 1
        ? parts[instancesIdx + 1]
        : undefined;
    return {
      collaboration_id: url_collaboration_id,
      collaboration_instance_id: url_instance_id,
      file_name: fileBlock.url?.split("/").pop(),
    };
  };

  const openFile = async (fileBlock: FileBlock) => {
    setLoadingFileName(fileBlock.fileName);
    try {
      const resp = await getFileURL(getFileObj(fileBlock));
      const uri = resp?.url ?? fileBlock.url;
      const ext = fileBlock.fileName.toLowerCase().split('.').pop() || '';
      const mimeType = ext === 'pdf'
        ? 'application/pdf'
        : ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
          ? `image/${ext === 'jpg' ? 'jpeg' : ext}`
          : undefined;
      canvas.loadDocument({ uri, title: fileBlock.fileName, ...(mimeType && { mimeType }) });
    } finally {
      setLoadingFileName(null);
    }
  };

  const downloadFile = async (fileBlock: FileBlock) => {
    setLoadingFileName(fileBlock.fileName);
    try {
      const resp = await getFileURL(getFileObj(fileBlock));
      if (resp?.url) {
        window.open(resp.url, '_blank');
      }
    } finally {
      setLoadingFileName(null);
    }
  }

  const resolveImageUrl = async (url: string): Promise<string> => {
    const resp = await getFileURL(getFileObj({ url, fileName: url.split('/').pop() || '' } as FileBlock));
    return resp?.url ?? url;
  };

  const isChatAttachmentCitation = (citation: DocumentCitationBlock) => {
    const parts = citation.sourceId?.split("/") ?? [];
    return parts.includes("collaborations") && parts.includes("instances");
  };

  const getCitationTitle = (citation: DocumentCitationBlock) =>
    citation.documentFilename || citation.title || citation.uri;

  const getCitationPage = (citation: DocumentCitationBlock) => {
    if (citation.chunkPage === undefined || citation.chunkPage === null) return undefined;
    const page = typeof citation.chunkPage === 'number'
      ? citation.chunkPage
      : Number.parseInt(String(citation.chunkPage), 10);
    return Number.isFinite(page) && page > 0 ? page : undefined;
  };

  const getRagSourceRequest = (citation: DocumentCitationBlock) => ({
    document_storage_account: citation.documentStorageAccount,
    document_container: citation.documentContainer,
    document_path: citation.documentPath || citation.documentFilename || citation.uri,
    document_filename: citation.documentFilename || citation.uri,
  });

  const resolveCitationPreviewUrl = async (citation: DocumentCitationBlock): Promise<string> => {
    if (citation.documentContainer) {
      const blob = await getRagSourceBlob(getRagSourceRequest(citation));
      if (citationPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(citationPreviewObjectUrlRef.current);
      }
      const objectUrl = URL.createObjectURL(blob);
      citationPreviewObjectUrlRef.current = objectUrl;
      return objectUrl;
    }
    return resolveCitationUrl(citation);
  };

  const resolveCitationUrl = async (citation: DocumentCitationBlock): Promise<string> => {
    if (citation.documentContainer) {
      const resp = await getRagSourceURL(getRagSourceRequest(citation));
      if (resp?.url) return resp.url;
    }

    if (isChatAttachmentCitation(citation)) {
      const parts = citation.sourceId?.split("/") ?? [];
      const collaborationsIdx = parts.indexOf("collaborations");
      const instancesIdx = parts.indexOf("instances");
      const resp = await getFileURL({
        collaboration_id: collaborationsIdx >= 0 ? parts[collaborationsIdx + 1] : undefined,
        collaboration_instance_id: instancesIdx >= 0 ? parts[instancesIdx + 1] : undefined,
        file_name: citation.sourceId?.split("/").pop(),
      });
      if (resp?.url) return resp.url;
    }

    if (citation.uri?.startsWith("http://") || citation.uri?.startsWith("https://")) {
      return citation.uri;
    }
    return citation.sourceId;
  };

  const viewCitation = async (citation: DocumentCitationBlock) => {
    setLoadingFileName(citation.uri);
    try {
      const uri = await resolveCitationPreviewUrl(citation);
      const page = getCitationPage(citation);
      canvas.loadDocument({
        uri,
        title: getCitationTitle(citation),
        mimeType: 'application/pdf',
        sourceId: citation.sourceId,
        ...(page && { page }),
      });
    } finally {
      setLoadingFileName(null);
    }
  };

  const downloadCitation = async (citation: DocumentCitationBlock) => {
    setLoadingFileName(citation.uri);
    try {
      const uri = await resolveCitationUrl(citation);
      window.open(uri, '_blank');
    } finally {
      setLoadingFileName(null);
    }
  };

  const isUserMessage = align === "right";
  const isAssistantMessage = message.sender.role === 'assistant';
  const isSystemMessage = systemMessage;
  const senderColor = !isUserMessage && !isAssistantMessage && !isSystemMessage
    ? getSenderColor(message.sender.id)
    : null;

  // Check feature flag for reactions - default to true if flag doesn't exist
  const allFlags = featureFlagService.getAllFlags();
  const enableReactions = true;

  // Filter reactions for AI assistant messages
  const reactionsToShow = isAssistantMessage
    ? ['👍', '👎']
    : availableReactions;

  return (
    <Box
      ref={messageRef}
      sx={{
        display: 'flex',
        justifyContent: isSystemMessage ? 'center' : (isUserMessage ? 'flex-end' : 'flex-start'),
        mb: 4,
        px: 2,
        // Apply blur effect for pending messages
        ...(message.isPending && {
          opacity: 0.6,
          filter: 'blur(0.5px)',
          transition: 'opacity 0.3s ease, filter 0.3s ease',
        }),
      }}
    >
      {/* System Message */}
      {isSystemMessage && (
        <MessageBubble
          elevation={0}
          isUser={false}
          isAssistant={false}
          isSystem={true}
          sx={{
            maxWidth: '60%',
          }}
        >
          <InfoIcon color="info" fontSize="small" />
          <Typography variant="body2" data-testid="message-text" sx={{ textAlign: 'center' }}>
            <ContentBlockRenderer
              blocks={displayBlocks}
              onFileOpen={openFile}
              onFileDownload={downloadFile}
              onCitationView={viewCitation}
              onCitationDownload={downloadCitation}
              onResolveImageUrl={resolveImageUrl}
              userMap={allUsersMap}
              loadingFileName={loadingFileName}
              isStreaming={isStreaming}
            />
          </Typography>
        </MessageBubble>
      )}

      {/* Regular Messages (Human/AI) */}
      {!isSystemMessage && (
        <Stack direction="row" spacing={1} alignItems="flex-end" maxWidth="80%">
          {!isUserMessage && (
            <Avatar
              alt={message.sender.displayName}
              src={message.sender.avatarUrl || undefined}
              sx={{
                width: 32,
                height: 32,
                ...(isAssistantMessage && { bgcolor: ASSISTANT_COLOR, color: '#fff' }),
                ...(senderColor && { bgcolor: senderColor.avatarBg, color: senderColor.avatarFg }),
              }}
            >
              {isAssistantMessage ? <SmartToyIcon fontSize="small" /> : message.sender.displayName?.charAt(0).toUpperCase()}
            </Avatar>
          )}

          <Stack spacing={0.5} flex={1}>
            {/* Author name and timestamp */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={isUserMessage ? 'flex-end' : 'flex-start'}
              onMouseDown={(e) => e.preventDefault()}
              sx={{ userSelect: 'none' }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary'
                }}
              >
                {isUserMessage ? "You" : message.sender.displayName}
              </Typography>
              {isAssistantMessage && !isUserMessage && (
                <Chip
                  size="small"
                  label="ASSISTANT"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    borderRadius: '4px',
                    bgcolor: alpha(ASSISTANT_COLOR, 0.12),
                    color: '#6D28D9',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontSize: '0.7rem' }}
              >
                {new Date(message.createdDateTime).toLocaleString()}
              </Typography>
            </Stack>

            {/* Message bubble */}
            <Box position="relative">
              <MessageBubble
                elevation={1}
                isUser={isUserMessage}
                isAssistant={isAssistantMessage && !isUserMessage}
                isSystem={false}
                accentColor={senderColor?.accent}
                data-testid="message-text"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={(e) => {
                  if (e.buttons !== 0) return;
                  const sel = window.getSelection();
                  if (sel && sel.toString().length > 0 && messageRef.current?.contains(sel.anchorNode)) return;
                  setIsHovered(false);
                }}
                sx={{
                  ...(isAssistantMessage && message.aiMetadata && {
                    paddingRight: 3,
                  })
                }}
              >
                <ContentBlockRenderer
                  blocks={displayBlocks}
                  onFileOpen={openFile}
                  onFileDownload={downloadFile}
                  onCitationView={viewCitation}
                  onCitationDownload={downloadCitation}
                  onResolveImageUrl={resolveImageUrl}
                  userMap={allUsersMap}
                  loadingFileName={loadingFileName}
                  isStreaming={isStreaming}
                  accentColor={isAssistantMessage ? ASSISTANT_COLOR : undefined}
                />
                {streamError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '0.8rem' }}>
                    Agent response failed to load.
                  </Typography>
                )}

                {/* AI Metadata Icon - Only for assistant messages with metadata */}
                {isAssistantMessage && message.aiMetadata && (
                  <IconButton
                    size="small"
                    onMouseEnter={handleAiMetadataMouseEnter}
                    onMouseLeave={handleAiMetadataMouseLeave}
                    aria-label="View AI response details"
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      padding: 0.5,
                      opacity: 0.6,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      }
                    }}
                  >
                    <InfoIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                )}
              </MessageBubble>

              {/* Reaction menu - only for non-user messages */}
              {enableReactions && !isUserMessage && (
                <Fade in={(showReactionsMenu || isHovered) && !isHoveringAiMetadata}>
                  <ReactionsContainer
                    ref={reactionMenuRef}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={(e) => {
                      if (e.buttons !== 0) return;
                      const sel = window.getSelection();
                      if (sel && sel.toString().length > 0 && messageRef.current?.contains(sel.anchorNode)) return;
                      setIsHovered(false);
                    }}
                    sx={{
                      position: 'absolute',
                      top: -45,
                      left: 0,
                      zIndex: 10,
                      pointerEvents: (showReactionsMenu || isHovered) && !isHoveringAiMetadata ? 'auto' : 'none',
                    }}
                  >
                    {reactionsToShow.map((emoji, index) => (
                      <ReactionButton
                        key={index}
                        size="small"
                        onClick={() => handleReaction(emoji)}
                        title={`React with ${emoji}`}
                      >
                        {emoji}
                      </ReactionButton>
                    ))}
                  </ReactionsContainer>
                </Fade>
              )}
            </Box>

            {/* Reactions display */}
            {enableReactions && Object.entries(message.interactions.reactions).some(([_, userIds]) => userIds.length > 0) && (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                justifyContent={isUserMessage ? 'flex-end' : 'flex-start'}
              >
                {Object.entries(message.interactions.reactions).map(([emoji, userIds]) => {
                  const hasUserReacted = userIds.includes(uId);
                  return (
                    userIds.length > 0 && (
                      <Chip
                        key={emoji}
                        size="small"
                        onClick={() => handleReaction(emoji)}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{emoji}</span>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              {userIds.length}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          height: 24,
                          cursor: 'pointer',
                          backgroundColor: (theme) =>
                            hasUserReacted
                              ? alpha(theme.palette.primary.main, 0.3)
                              : alpha(theme.palette.primary.main, 0.1),
                          border: (theme) =>
                            hasUserReacted
                              ? `2px solid ${theme.palette.primary.main}`
                              : 'none',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                          }
                        }}
                      />
                    )
                  );
                })}
              </Stack>
            )}
          </Stack>

          {isUserMessage && (
            <Avatar
              alt={message.sender.displayName}
              src={message.sender.avatarUrl}
              sx={{ width: 32, height: 32 }}
            >
              {message.sender.displayName?.charAt(0).toUpperCase()}
            </Avatar>
          )}
        </Stack>
      )}

      {/* Feedback Dialog for Assistant Reactions */}
      <Dialog
        open={showFeedbackDialog}
        onClose={handleFeedbackCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Provide Feedback {selectedEmoji}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help us improve by sharing your thoughts (optional)
          </Typography>
          <Box sx={{ mb: 3 }}>
            {selectedEmoji === '👍' ? (
              <>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                  How helpful was this response?
                </Typography>
                <Rating
                  name="positive-rating"
                  value={rating}
                  onChange={(_, newValue) => {
                    setRating(newValue);
                  }}
                  size="large"
                  sx={{ fontSize: '2rem' }}
                />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                  How unhelpful was this response?
                </Typography>
                <Rating
                  name="negative-rating"
                  value={rating}
                  onChange={(_, newValue) => {
                    setRating(newValue);
                  }}
                  size="large"
                  icon={<span style={{ fontSize: '2rem' }}>😠</span>}
                  emptyIcon={<span style={{ fontSize: '2rem', opacity: 0.3 }}>😠</span>}
                />
              </>
            )}
          </Box>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="What did you think about this response?"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleFeedbackCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleFeedbackSubmit} variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Metadata Popover */}
      <Popover
        open={Boolean(aiMetadataAnchor) && Boolean(message.aiMetadata)}
        anchorReference="anchorPosition"
        anchorPosition={aiMetadataAnchor || { top: 0, left: 0 }}
        onClose={handleAiMetadataClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              maxWidth: 320,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2,
              pointerEvents: 'auto',
            },
            onMouseEnter: () => {
              if (aiMetadataHoverTimeout) {
                clearTimeout(aiMetadataHoverTimeout);
                setAiMetadataHoverTimeout(null);
              }
            },
            onMouseLeave: handleAiMetadataClose,
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <InfoIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              AI Response Details
            </Typography>
          </Box>

          {/* Model ID Section */}
          {message.aiMetadata && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                  MODEL
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <SmartToyIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {message.aiMetadata.modelId}
                  </Typography>
                </Box>
              </Box>

              {/* Token Usage Section */}
              {message.aiMetadata.tokenUsage && (
                              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                  TOKEN USAGE
                </Typography>
                <Stack spacing={0.75}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      Prompt
                    </Typography>
                    <Chip
                      label={message.aiMetadata.tokenUsage.prompt.toLocaleString()}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1),
                        color: 'info.main'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      Completion
                    </Typography>
                    <Chip
                      label={message.aiMetadata.tokenUsage.completion.toLocaleString()}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1),
                        color: 'success.main'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.85rem', fontWeight: 600 }}>
                      Total
                    </Typography>
                    <Chip
                      label={message.aiMetadata.tokenUsage.total.toLocaleString()}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                        color: 'primary.main'
                      }}
                    />
                  </Box>
                </Stack>
              </Box>
              )}

            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default MessageComponent;
